import { Select, TextInput, TextInputProps, SelectProps } from '@mantine/core';
import { memo, useEffect, useState } from 'react';
import { useController, useFormContext } from 'react-hook-form';
import { Icon } from '@dealstack/frontend/shared/components/fundament/Icon/Icon';
import { InputContainer, InputWrapper } from './styled';
import { CurrencyType } from '@dealstack/shared/lib/generated';
import { useTranslationValuations } from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks';
import { formatSummaryDecimalString } from '@dealstack/frontend/app/pages/Valuations/components/table/utils/formatting';
import { VALUATION_ELEMENT_ID } from '@dealstack/shared/tests/testElementIds';

const DropdownIcon = <Icon variant="caret-down" />;

interface Props {
	name: string;
	className?: string;
	placeholder?: string;
	label?: string;
	disabled?: boolean;
	currencyOptions?: CurrencyType[];
	onChange?: (value: CurrencyInputValue) => void;
	onBlur?: (event: React.FocusEvent) => void;
	onFocus?: (event: React.FocusEvent) => void;
	styles?: {
		textInput?: TextInputProps['styles'];
		select?: SelectProps['styles'];
	};
}

interface CurrencyInputValue {
	currency: CurrencyType;
	currencyAmount: string;
}

const isNumeric = (str: string) => {
	if (typeof str !== 'string') {
		return false;
	}
	// First check filters out most non-numeric strings, but keeps stuff like false and blank strings.
	// Second check filters out false and blank strings.
	return isFinite(+str) && isFinite(parseFloat(str));
};

const strToDisplayValue = (str: string | undefined) => {
	const strValue = str ?? '0';
	if (isNumeric(strValue)) {
		return formatSummaryDecimalString(strValue, true);
	}
	return strValue;
};

const CurrencyInput = ({
	name,
	className,
	placeholder,
	label,
	disabled,
	currencyOptions = Object.values(CurrencyType),
	onChange,
	onBlur,
	onFocus,
	styles,
}: Props) => {
	const { trigger, resetField, getValues } = useFormContext();
	const tValuations = useTranslationValuations();
	const {
		field: currencyAndAmountField,
		fieldState: { invalid: isFieldInvalid },
	} = useController({
		name,
	});

	const value = {
		currencyAmount: '',
		currency: (getValues(name)?.currency as CurrencyType) ?? CurrencyType.EUR,
		...currencyAndAmountField.value,
	};

	const amount: string = value.currencyAmount;
	const currency: CurrencyType = value.currency;
	const [displayValue, setDisplayValue] = useState(strToDisplayValue(amount));
	const [isEditing, setIsEditing] = useState(false);
	const isFieldValid = !isFieldInvalid;

	useEffect(() => {
		if (!isEditing) {
			setDisplayValue(strToDisplayValue(amount));
		}
	}, [amount, isEditing]);

	if (amount === undefined || amount === null || currency === undefined || currency === null) {
		return (
			<InputWrapper className={className}>
				{label && <div className="mantine-InputWrapper-label">{label}</div>}
				<InputContainer $disabled>
					<Select
						className="currency-select"
						data={currencyOptions}
						disabled
						rightSection={DropdownIcon}
						styles={styles?.select}
					/>
					<TextInput
						className="amount-input"
						placeholder="Field error: Undefined input"
						disabled
						styles={styles?.textInput}
					/>
				</InputContainer>
			</InputWrapper>
		);
	}

	const notifyFieldChange = async (newCurrency: CurrencyType, newAmount: string) => {
		currencyAndAmountField.onChange({
			currency: newCurrency,
			currencyAmount: newAmount,
		});

		const isValid = await trigger(name);
		if (isValid) {
			onChange?.({ currency: newCurrency, currencyAmount: newAmount });
		}
	};

	const handleCurrencySelect = async (newCurrency: CurrencyType) => {
		await notifyFieldChange(newCurrency, amount);
	};

	const handleAmountChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
		const inputValue = e.target.value;
		setDisplayValue(inputValue);
		await notifyFieldChange(currency, inputValue);
	};

	const handleAmountBlur = async (e: React.FocusEvent<HTMLInputElement>) => {
		if (!isFieldValid) {
			resetField(name);
		}
		setDisplayValue(strToDisplayValue(getValues(name)?.currencyAmount));
		onBlur?.(e);
		setIsEditing(false);
	};

	const handleAmountFocus = (e: React.FocusEvent<HTMLInputElement>) => {
		setDisplayValue(amount);
		onFocus?.(e);
		setIsEditing(true);
	};

	return (
		<InputWrapper className={className}>
			{label && <div className="mantine-InputWrapper-label">{label}</div>}
			<InputContainer $disabled={!!disabled}>
				<Select
					data-testid={VALUATION_ELEMENT_ID.INPUT_CURRENCY}
					className="currency-select"
					value={currency}
					onChange={handleCurrencySelect}
					data={currencyOptions}
					disabled={disabled}
					rightSection={DropdownIcon}
					onBlur={(e) => onBlur?.(e)}
					onFocus={(e) => {
						onFocus?.(e);
					}}
					styles={styles?.select}
				/>
				<TextInput
					data-testid={VALUATION_ELEMENT_ID.INPUT_AMOUNT}
					className="amount-input"
					value={displayValue}
					onChange={handleAmountChange}
					onBlur={handleAmountBlur}
					onFocus={handleAmountFocus}
					error={!isFieldValid}
					placeholder={placeholder ?? tValuations('currency_input.placeholder')}
					disabled={disabled}
					styles={styles?.textInput}
				/>
			</InputContainer>
		</InputWrapper>
	);
};
const _CurrencyInputExport = memo(CurrencyInput);

export { _CurrencyInputExport as CurrencyInput };
